import React, { useCallback, useMemo, useState } from "react";
import { Box, makeStyles, Typography } from "@material-ui/core";

import {
  FINANCIAL_REPORT_SUBTITLES,
  REPORT_ITEM_SUB_TYPE,
  REPORT_ITEM_TYPE
} from "../../../../../constants";
import CustomCardHeader from "../../CustomCardHeader";
import { asCurrency } from "../../../../../utils";
import NewAdvertisingIncomeDialog from "./NewAdvertisingIncomeDialog";
import EditAdvertisingIncomeDialog from "./EditAdvertisingIncomeDialog";
import Button from "components/CustomButtons/Button";
import Edit from "@material-ui/icons/Edit";

const useStyles = makeStyles({
  actions: {
    margin: "0 0 0 5px",
    padding: 5,
    "& svg": {
      margin: 0
    }
  }
});

const AdvertisingIncome = ({
  reportID,
  data,
  chapterID,
  year,
  createReportItem,
  updateReportItem,
  initialReportItems,
  removeReportItem,
  onRefetch,
  onHelp,
  onRedirect,
  isEditable
}) => {
  const classes = useStyles();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const items = useMemo(() => {
    return data.filter(
      item => item.reportItemSubTypeID === REPORT_ITEM_SUB_TYPE.ADVERTISING
    );
  }, [data]);

  const onSaveEditDialog = useCallback(
    async ({ items, editedItems, removedItems }) => {
      if (reportID === "add") {
        const body = {
          chapterID,
          year,
          items: items.map(({ id, ...item }) => ({
            ...item,
            reportItemTypeID: REPORT_ITEM_TYPE.REVENUE,
            reportItemSubTypeID: REPORT_ITEM_SUB_TYPE.ADVERTISING
          }))
        };
        const _reportID = await initialReportItems(body);
        onRedirect(_reportID);
      } else {
        await Promise.all(
          editedItems.map(({ id, ...item }) =>
            updateReportItem(id, {
              ...item,
              reportID,
              reportItemTypeID: REPORT_ITEM_TYPE.REVENUE,
              reportItemSubTypeID: REPORT_ITEM_SUB_TYPE.ADVERTISING
            })
          )
        );
        await Promise.all(removedItems.map(({ id }) => removeReportItem(id)));
      }
      onRefetch();
      setEditDialogOpen(false);
    },
    [reportID]
  );

  const onSaveCreateDialog = useCallback(
    async item => {
      const body = {
        ...item,
        reportItemTypeID: REPORT_ITEM_TYPE.REVENUE,
        reportItemSubTypeID: REPORT_ITEM_SUB_TYPE.ADVERTISING
      };

      if (reportID === "add") {
        const _reportID = await initialReportItems({
          chapterID: chapterID,
          year: year,
          items: [body]
        });
        onRedirect(_reportID);
      } else {
        await createReportItem({ ...body, reportID });
      }

      onRefetch();
      setCreateDialogOpen(false);
    },
    [reportID, chapterID, year]
  );

  return (
    <Box mb={3}>
      <CustomCardHeader
        label={FINANCIAL_REPORT_SUBTITLES.Advertising}
        onHelp={() =>
          onHelp(REPORT_ITEM_TYPE.REVENUE, REPORT_ITEM_SUB_TYPE.ADVERTISING)
        }
        extraContent={
          items.length !== 0 &&
          isEditable && (
            <Button
              simple
              color="primary"
              className={classes.actions}
              onClick={() => setEditDialogOpen(true)}
            >
              <Edit />
            </Button>
          )
        }
      />
      {items.map(item => (
        <Box
          display="flex"
          justifyContent="space-between"
          px={1}
          py={0.5}
          key={`revenue-${item.id}`}
        >
          <Typography>{item.description}</Typography>
          <Typography>{asCurrency(item.amount || 0)}</Typography>
        </Box>
      ))}
      {isEditable && (
        <Box ml={3}>
          <a onClick={() => setCreateDialogOpen(true)}>
            <strong>{"Add Advertising Income"}</strong>
          </a>
        </Box>
      )}
      <NewAdvertisingIncomeDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSave={onSaveCreateDialog}
      />
      <EditAdvertisingIncomeDialog
        open={editDialogOpen}
        data={items}
        onClose={() => setEditDialogOpen(false)}
        onSave={onSaveEditDialog}
      />
    </Box>
  );
};

export default AdvertisingIncome;
